import { useState } from 'react';
import { Avatar, Empty, ErrorBanner, Loading, PageHead } from '../components/ui';
import { formatDate } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { PeopleSummary, PersonRow } from '../lib/types';
import { Tabs } from './am/amUi';
import { JobDescriptions } from './people/JobDescriptions';
import { PersonWorkspace } from './people/PersonWorkspace';
import { TrainingMatrix } from './people/TrainingMatrix';

/**
 * People — the HR Toolbox, moved in from `C:\Development\HR Toolbox`.
 *
 * Everything that app did: the people directory with its full profile, the
 * training matrix with per-person completion, performance reviews built from
 * People Templates, the job description library and the recruiting question
 * bank with saved scorecards.
 *
 * The one thing that did NOT come across is its employee table. People here
 * are `core.Employee` rows — see migration 032 — which is the whole reason the
 * app estate shares a database. That is also why this page never offers to add
 * or remove somebody: it links to the Employees page instead.
 */

/* Recruiting was a tab here; it is its own page now (RecruitingPage, 174). */
type Tab = 'people' | 'training' | 'documents';

const TABS: { key: Tab; label: string }[] = [
  { key: 'people', label: 'People' },
  { key: 'training', label: 'Training' },
  { key: 'documents', label: 'Job descriptions' },
];

/**
 * `isAdmin` decides whether the People tab is drawn at all. The directory and
 * every profile behind it are admin-only (requireAdmin on /people/directory),
 * so anyone else given this page gets Training and Job descriptions.
 */
export function PeoplePage({ isAdmin }: { isAdmin: boolean }) {
  const summary = useResource<PeopleSummary>('/people/summary');
  const tabs = isAdmin ? TABS : TABS.filter((t) => t.key !== 'people');
  const [tab, setTab] = useState<Tab>(isAdmin ? 'people' : 'training');
  const [employeeId, setEmployeeId] = useState<number | null>(null);

  if (isAdmin && employeeId !== null) {
    return (
      <PersonWorkspace
        employeeId={employeeId}
        onBack={() => {
          setEmployeeId(null);
          summary.reload();
        }}
        onChanged={summary.reload}
      />
    );
  }

  return (
    <>
      <PageHead
        title="People"
        lede="Everyone here, the training each role owes, the reviews they are due, the job descriptions they signed, and the question bank we hire against. The directory itself lives in the shared database — this is what we know about the people in it."
      />

      {summary.error && <ErrorBanner message={summary.error} />}
      {summary.data && <PeopleHero summary={summary.data} />}

      <Tabs<Tab> tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'people' && isAdmin && (
        <PeopleList
          summary={summary.data}
          onOpen={(id) => {
            setEmployeeId(id);
            window.scrollTo({ top: 0 });
          }}
        />
      )}
      {tab === 'training' && <TrainingMatrix onChanged={summary.reload} />}
      {tab === 'documents' && <JobDescriptions onChanged={summary.reload} />}
    </>
  );
}

/**
 * The four figures the module hangs off.
 *
 * Reviews due leads, because it is the one that decays on its own: nobody has
 * to do anything for it to get worse. Required training is stated as a
 * fraction rather than a percentage — "142 of 180" says how much work is left,
 * and "79%" does not.
 */
function PeopleHero({ summary: s }: { summary: PeopleSummary }) {
  return (
    <div className="grid grid-4 mb-4">
      <div className="stat">
        <div className="label">People</div>
        <div className="figure">{s.People}</div>
        <div className="foot">active in the directory</div>
      </div>
      <div className="stat">
        <div className="label">Reviews due</div>
        <div className={`figure${s.ReviewsDue > 0 ? ' accent' : ''}`}>{s.ReviewsDue}</div>
        <div className="foot">no completed review in 12 months</div>
      </div>
      <div className="stat">
        <div className="label">Reviews in draft</div>
        <div className="figure">{s.ReviewsOpen}</div>
        <div className="foot">started, not yet complete</div>
      </div>
      <div className="stat">
        <div className="label">Required training</div>
        <div className="figure">
          {s.RequiredTrainingDone}
          <span className="subtle"> / {s.RequiredTraining}</span>
        </div>
        <div className="foot">completed across everyone with a template</div>
      </div>
    </div>
  );
}

function PeopleList({
  summary,
  onOpen,
}: {
  summary: PeopleSummary | null;
  onOpen: (employeeId: number) => void;
}) {
  const [search, setSearch] = useState('');
  const people = useResource<PersonRow[]>(
    `/people/directory${search.trim() ? `?search=${encodeURIComponent(search.trim())}` : ''}`,
  );

  return (
    <>
      <div className="row-between wrap mb-4">
        <input
          className="input"
          style={{ maxWidth: 360 }}
          placeholder="Search by name, email, department or role…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <p className="xs subtle">
          People are added and changed on the Employees page — this module reads the shared
          directory and never writes to it.
        </p>
      </div>

      {people.loading && <Loading label="Loading people" />}
      {people.error && <ErrorBanner message={people.error} />}

      {!people.loading &&
        (people.data?.length === 0 ? (
          <Empty title={search ? `Nobody matches “${search}”` : 'Nobody in the directory yet'} />
        ) : (
          <div className="table-wrap card">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Start date</th>
                  <th>Template</th>
                  <th>Last review</th>
                  <th className="num">Required training</th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {(people.data ?? []).map((p) => (
                  <tr key={p.EmployeeId}>
                    <td>
                      <span className="row">
                        <Avatar name={p.FullName} />
                        <span>
                          <span className="strong">{p.FullName}</span>
                          {p.PreferredName && <span className="xs subtle"> “{p.PreferredName}”</span>}
                          <div className="xs subtle">{p.Email ?? 'No email'}</div>
                        </span>
                      </span>
                    </td>
                    <td>
                      {p.RoleTitle ?? <span className="subtle">—</span>}
                      {p.DepartmentName && <div className="xs subtle">{p.DepartmentName}</div>}
                    </td>
                    <td>
                      {p.StartDate ? formatDate(p.StartDate) : <span className="subtle">—</span>}
                    </td>
                    <td>
                      {p.TemplateName ?? <span className="xs subtle">Not assigned</span>}
                      {p.OpenReviewCount > 0 && (
                        <div>
                          <span className="badge badge-neutral">
                            {p.OpenReviewCount} in draft
                          </span>
                        </div>
                      )}
                    </td>
                    <td>{formatDate(p.LastReviewDate)}</td>
                    <td className="num">
                      {p.RequiredTraining === 0 ? (
                        <span className="subtle">—</span>
                      ) : (
                        <span
                          className={
                            p.RequiredTrainingDone < p.RequiredTraining ? 'mono flagged' : 'mono'
                          }
                        >
                          {p.RequiredTrainingDone} / {p.RequiredTraining}
                        </span>
                      )}
                    </td>
                    <td className="actions">
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onOpen(p.EmployeeId)}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      {summary && (
        <div className="grid grid-2 mt-6">
          <section className="card">
            <div className="card-head">
              <div className="grow">
                <h3>Reviews due</h3>
                <div className="sub">No completed review in the last 12 months.</div>
              </div>
              <span className="mono">{summary.reviewsDue.length}</span>
            </div>
            <div className="card-body">
              {summary.reviewsDue.length === 0 ? (
                <p className="muted small">Everyone is up to date.</p>
              ) : (
                summary.reviewsDue.map((r) => (
                  <button
                    key={r.EmployeeId}
                    type="button"
                    className="mini-row row-between wrap"
                    style={{ width: '100%', background: 'none', border: 0, cursor: 'pointer', textAlign: 'left' }}
                    onClick={() => onOpen(r.EmployeeId)}
                  >
                    <span className="grow">
                      <span className="strong">{r.FullName}</span>
                      {r.RoleTitle && <span className="xs subtle"> · {r.RoleTitle}</span>}
                    </span>
                    <span className="xs muted">
                      {r.LastReviewDate ? `Last reviewed ${formatDate(r.LastReviewDate)}` : 'Never reviewed'}
                    </span>
                  </button>
                ))
              )}
            </div>
          </section>

          <section className="card">
            <div className="card-head">
              <div className="grow">
                <h3>Reviews in progress</h3>
                <div className="sub">Started but not yet marked complete.</div>
              </div>
              <span className="mono">{summary.openReviews.length}</span>
            </div>
            <div className="card-body">
              {summary.openReviews.length === 0 ? (
                <p className="muted small">Nothing in draft.</p>
              ) : (
                summary.openReviews.map((r) => (
                  <button
                    key={r.ReviewId}
                    type="button"
                    className="mini-row row-between wrap"
                    style={{ width: '100%', background: 'none', border: 0, cursor: 'pointer', textAlign: 'left' }}
                    onClick={() => onOpen(r.EmployeeId)}
                  >
                    <span className="grow">
                      <span className="strong">{r.EmployeeName}</span>
                      <span className="xs subtle"> · {r.TemplateName ?? 'Blank review'}</span>
                    </span>
                    <span className="xs muted">
                      {r.ReviewDate ? formatDate(r.ReviewDate) : 'No date set'}
                    </span>
                  </button>
                ))
              )}
            </div>
          </section>
        </div>
      )}
    </>
  );
}
